// src/utils/statusDetector.js
import { statusMonitor } from './StatusMonitor';
import { STATUS_LEVELS } from './statusDefinitions';

const THRESHOLDS = {
  HIGH_BACKLOG: 50,
  CRITICAL_BACKLOG: 200,
  SLOW_CONNECTION: 3000,
  MAX_CONSECUTIVE_FAILURES: 5,
  SYNC_DELAY_WARNING: 5,
  SYNC_DELAY_CRITICAL: 15
};

export class StatusDetector {
  constructor() {
    this.activeIssues = new Set();
    this.lastSyncSuccess = Date.now();
    this.consecutiveFailures = 0;
    this.lastErrorType = null;
    this.previousErrorType = null;
  }

  async checkNetworkQuality() {
    if (!navigator.onLine) {
      this.activeIssues.add('offline');
      console.log('Device is offline - setting offline status');
      return;
    }

    this.activeIssues.delete('offline');
    this.activeIssues.delete('slow-connection');
    
    console.log('Device is online');
  }

  async checkDataBacklog(getUnsyncedCountFunction) {
    try {
      const count = await getUnsyncedCountFunction();
      statusMonitor.metrics.unsyncedCount = count;

      this.activeIssues.delete('healthy-pending');
      this.activeIssues.delete('high-backlog');
      this.activeIssues.delete('critical-backlog');

      if (count > THRESHOLDS.CRITICAL_BACKLOG) {
        this.activeIssues.add('critical-backlog');
      } else if (count > THRESHOLDS.HIGH_BACKLOG) {
        this.activeIssues.add('high-backlog');
      } else if (count > 0) {
        this.activeIssues.add('healthy-pending');
      }

      return count;
    } catch (error) {
      console.error('Error checking data backlog:', error);
      this.activeIssues.add('indexeddb-error');
      return 0;
    }
  }

  checkSyncDelay() {
    const minutesSinceLastSync = (Date.now() - this.lastSyncSuccess) / 60000;

    this.activeIssues.delete('sync-delayed');
    this.activeIssues.delete('sync-stopped');

    // CRITICAL: Don't add sync-delayed if there's already a more specific error
    const hasSpecificError = Array.from(this.activeIssues).some(issue => 
      issue.includes('rds') || 
      issue.includes('backend') || 
      issue === 'offline' || 
      issue === 'indexeddb-error' ||
      issue === 'critical-error'
    );

    if (hasSpecificError) {
      console.log('ℹ️ Skipping sync-delayed check - more specific error already present:', this.lastErrorType);
      return;
    }

    if (minutesSinceLastSync > THRESHOLDS.SYNC_DELAY_CRITICAL) {
      this.activeIssues.add('sync-stopped');
    } else if (minutesSinceLastSync > THRESHOLDS.SYNC_DELAY_WARNING) {
      this.activeIssues.add('sync-delayed');
    }
  }

  clearAllErrorStates() {
    const allErrorStates = [
      'slow-connection', 'rds-timeout', 'rds-error', 'rds-auth-error', 'rds-unreachable',
      'backend-5xx', 'backend-timeout', 'backend-overload', 'backend-down', 'backend-unreachable',
      'client-4xx', 'auth-error', 'data-validation-error',
      'critical-error', 'partial-sync',
      'indexeddb-error'
    ];
    
    allErrorStates.forEach(state => this.activeIssues.delete(state));
    
    console.log('🧹 Cleared all error states');
  }

  categorizeApiError(error, response) {
    this.clearAllErrorStates();
    
    console.log('🔍 Categorizing error:', {
      errorName: error.name,
      errorMessage: error.message,
      errorCode: error.code,
      responseStatus: response?.status,
      onLine: navigator.onLine
    });

    if (!navigator.onLine) {
      this.activeIssues.add('offline');
      this.lastErrorType = 'offline';
      console.log('❌ Error occurred while offline');
      return;
    }

    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      if (response?.status >= 500) {
        this.activeIssues.add('backend-timeout');
        this.lastErrorType = 'backend-timeout';
      } else {
        this.activeIssues.add('rds-timeout');
        this.lastErrorType = 'rds-timeout';
      }
      return;
    }

    if (response && response.status) {
      const status = response.status;

      if (status === 503) {
        this.activeIssues.add('backend-overload');
        this.lastErrorType = 'backend-overload';
      } else if (status >= 500) {
        this.activeIssues.add('backend-5xx');
        this.lastErrorType = 'backend-5xx';
      } else if (status === 401 || status === 403) {
        this.activeIssues.add('auth-error');
        this.lastErrorType = 'auth-error';
      } else if (status === 422) {
        this.activeIssues.add('data-validation-error');
        this.lastErrorType = 'data-validation-error';
      } else if (status >= 400) {
        this.activeIssues.add('client-4xx');
        this.lastErrorType = 'client-4xx';
      }
      return;
    }

    // if (error.message?.includes('Failed to fetch')) {
    //   console.log('🔌 Failed to fetch - backend likely down or unreachable');
    //   this.activeIssues.add('backend-unreachable');
    //   this.lastErrorType = 'backend-unreachable';
    //   return;
    // }

    if (error.message?.includes('NetworkError') || error.message?.includes('Network request failed')) {
      console.log('🌐 Network error - backend unreachable');
      this.activeIssues.add('backend-unreachable');
      this.lastErrorType = 'backend-unreachable';
      return;
    }

    if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
      console.log('❌ Connection refused - backend server not running');
      this.activeIssues.add('backend-down');
      this.lastErrorType = 'backend-down';
      return;
    }

    if (error.message?.includes('RDS') || error.code === 'ETIMEDOUT') {
      this.activeIssues.add('rds-error');
      this.lastErrorType = 'rds-error';
      return;
    }

    console.log('❓ Unknown error type, categorizing as critical');
    this.activeIssues.add('critical-error');
    this.lastErrorType = 'critical-error';
  }

  clearErrorStates() {
    const errorStates = [
      'slow-connection', 'rds-timeout', 'rds-error', 'rds-auth-error', 'rds-unreachable',
      'backend-5xx', 'backend-timeout', 'backend-overload', 'backend-down', 'backend-unreachable',
      'client-4xx', 'auth-error', 'data-validation-error',
      'critical-error', 'max-retries-exceeded', 'partial-sync',
      'indexeddb-error'
    ];
    
    errorStates.forEach(state => this.activeIssues.delete(state));
    
    if (navigator.onLine) {
      this.activeIssues.delete('offline');
    }

    this.lastErrorType = null;
    this.previousErrorType = null;
    console.log('✅ Cleared error states after successful sync');
  }

  handleSyncAttempt(success, metadata = {}) {
    if (success) {
      this.consecutiveFailures = 0;
      this.lastSyncSuccess = Date.now();
      this.lastErrorType = null;
      this.previousErrorType = null;
      
      if (navigator.onLine) {
        this.clearErrorStates();
        this.activeIssues.delete('max-retries-exceeded');
        this.activeIssues.delete('sync-delayed');
        this.activeIssues.delete('sync-stopped');
      } else {
        console.log('✅ Sync succeeded but device is now offline');
        this.activeIssues.add('offline');
      }
      
    } else {
      if (this.previousErrorType && this.previousErrorType !== this.lastErrorType) {
        console.log(`🔄 Error type changed from "${this.previousErrorType}" to "${this.lastErrorType}" - RESETTING failure counter`);
        this.consecutiveFailures = 1;
        this.activeIssues.delete('max-retries-exceeded');
      } else {
        this.consecutiveFailures++;
      }

      this.previousErrorType = this.lastErrorType;

      console.log(`📊 Consecutive failures: ${this.consecutiveFailures}, Last error type: ${this.lastErrorType}`);
      
      this.updateStatus(metadata);
      
      if (this.consecutiveFailures >= THRESHOLDS.MAX_CONSECUTIVE_FAILURES) {
        this.activeIssues.add('max-retries-exceeded');
        console.log(`⚠️ Max retries exceeded (${this.consecutiveFailures}). Underlying issue: ${this.lastErrorType}`);
        
        this.updateStatus(metadata);
      }
      
      return;
    }

    this.updateStatus(metadata);
  }

  updateStatus(metadata = {}) {
    if (!navigator.onLine) {
      this.activeIssues.add('offline');
    }

    const nonHealthyIssues = Array.from(this.activeIssues).filter(
      issue => !STATUS_LEVELS[issue]?.isHealthy
    );

    if (nonHealthyIssues.length === 0) {
      statusMonitor.setStatus('healthy', metadata);
      return;
    }

    // CRITICAL FIX: If max-retries-exceeded is present, show the underlying error instead
    if (nonHealthyIssues.includes('max-retries-exceeded') && this.lastErrorType && this.lastErrorType !== 'max-retries-exceeded') {
      console.log(`⚠️ Max retries exceeded, but displaying underlying error: ${this.lastErrorType}`);
      
      statusMonitor.setStatus(this.lastErrorType, {
        ...metadata,
        activeIssues: nonHealthyIssues,
        consecutiveFailures: this.consecutiveFailures,
        lastErrorType: this.lastErrorType,
        maxRetriesExceeded: true
      });
      return;
    }

    // CRITICAL FIX: If we have both sync-delayed/sync-stopped AND a more specific error,
    // ignore sync-delayed/sync-stopped and show the specific error
    const hasSpecificError = nonHealthyIssues.some(issue => 
      issue.includes('rds') || 
      issue.includes('backend') || 
      issue === 'offline' || 
      issue === 'indexeddb-error' ||
      issue === 'critical-error' ||
      issue === 'auth-error' ||
      issue === 'data-validation-error'
    );

    const issuesForPriority = nonHealthyIssues.filter(issue => {
      // If we have a specific error, exclude sync-delayed and sync-stopped from priority calculation
      if (hasSpecificError && (issue === 'sync-delayed' || issue === 'sync-stopped')) {
        console.log(`🔍 Excluding ${issue} from priority calculation - specific error present`);
        return false;
      }
      // If max-retries-exceeded and we have a lastErrorType, exclude max-retries from priority check
      if (issue === 'max-retries-exceeded' && this.lastErrorType && this.lastErrorType !== 'max-retries-exceeded') {
        return false;
      }
      return true;
    });

    // If after filtering we have no issues, show the first non-healthy issue
    if (issuesForPriority.length === 0 && nonHealthyIssues.length > 0) {
      console.warn('⚠️ All issues filtered out, showing first available issue');
      const criticalIssue = nonHealthyIssues[0];
      statusMonitor.setStatus(criticalIssue, {
        ...metadata,
        activeIssues: nonHealthyIssues,
        consecutiveFailures: this.consecutiveFailures,
        lastErrorType: this.lastErrorType
      });
      return;
    }

    const criticalIssue = issuesForPriority.reduce((highest, current) => {
      const currentPriority = STATUS_LEVELS[current]?.priority ?? 0;
      const highestPriority = STATUS_LEVELS[highest]?.priority ?? 0;
      return currentPriority > highestPriority ? current : highest;
    });

    console.log('🎯 Updating status to:', criticalIssue, 'Active issues:', nonHealthyIssues, 'Issues considered:', issuesForPriority);

    statusMonitor.setStatus(criticalIssue, {
      ...metadata,
      activeIssues: nonHealthyIssues,
      consecutiveFailures: this.consecutiveFailures,
      lastErrorType: this.lastErrorType
    });
  }

  // src/utils/statusDetector.js

async performFullHealthCheck(getUnsyncedCountFunction) {
  console.log('🔍 performFullHealthCheck called');
  
  await this.checkNetworkQuality();
  
  // CRITICAL: Always check data backlog, even when offline
  // Users need to see how much data is waiting to sync
  console.log('📊 Checking data backlog (works offline too)...');
  await this.checkDataBacklog(getUnsyncedCountFunction);
  
  if (!navigator.onLine) {
    console.log('❌ Device offline, skipping sync delay check');
    this.updateStatus({
      healthCheck: true,
      timestamp: new Date().toISOString(),
      offline: true
    });
    return;
  }
  
  this.checkSyncDelay();
  
  console.log('📊 After health check - activeIssues:', Array.from(this.activeIssues));
  
  this.updateStatus({
    healthCheck: true,
    timestamp: new Date().toISOString()
  });
}
  
}

export const statusDetector = new StatusDetector();